/**
 * Role-aware scope engine (04-03) — the testable heart of the dashboard's
 * role-aware behaviour.
 *
 * Given a scope TOKEN (from the role-permissions matrix §4, resolved per role
 * by `defaultScopeFor` in config/roles.ts), the signed-in IDENTITY, the target
 * REGISTER, and the register's RECORDS, return the subset that token permits the
 * role to see. Tiles (04-05), register views (04-07), and the closure trail
 * (04-08) all filter through this one module.
 *
 * IMPORTANT — this is PRESENTATION scoping over a delegated-Graph result
 * (04-CONTEXT.md §3), NOT a hard confidentiality boundary. It realizes the
 * matrix's intended VISIBILITY; it is not a security control (a determined
 * office user can read the unfiltered Graph response in devtools — the same
 * posture harmony-ap accepted). All 14 office staff already have site access.
 *
 * PURITY: this module imports only TYPES (no React, no Graph, no I/O) so it is
 * trivially unit-testable. The category subsets and the CM→BU map are INJECTED
 * via `ScopeConfig` / `Identity` — never literals here — so 04-04's ratified
 * values (04-04-rbac-resolved.md §1/§4) plug in without editing this file.
 *
 * FAIL-CLOSED: an unknown / undefined token returns `[]`, never the full set.
 * Mis-defaulting to "show everything" is the dangerous bug, so the default
 * branch hides rather than reveals.
 */
import type { ScopeToken } from '../config/roles';
import type { BusinessUnit, RegisterKey, RegisterRecord } from '../types/qms';

// ---------------------------------------------------------------------------
// Inputs the engine needs beyond the records themselves
// ---------------------------------------------------------------------------

/**
 * The minimal signed-in identity the scope engine filters against. Derived from
 * the 04-04 `AuthUser` at the call site (kept minimal + pure here — no auth/MSAL
 * import). `upn` is matched against a record's owner field; `businessUnits` is
 * the Care Manager's resolved BU set (empty === the no-per-CM-map fallback, §8).
 */
export interface Identity {
  /** The user's UPN / login identity (matched against owner fields). */
  upn: string;
  /**
   * The business units this identity manages, for the `Portfolio` token. EMPTY
   * means "no per-CM BU map yet" → the grouped fallback (see Portfolio below).
   */
  businessUnits: BusinessUnit[];
}

/**
 * Injected scope configuration — the parts of the matrix that 04-04 ratifies at
 * build time (04-04-rbac-resolved.md §4). Passed in (NOT hard-coded) so the
 * ratified subsets / a future native `FinanceRelevant` flag flow in without
 * touching this engine.
 */
export interface ScopeConfig {
  /**
   * Category values treated as finance-relevant (04-04 §4a). Approximates the
   * `Finance-relevant` token until a native `FinanceRelevant` flag lands.
   */
  financeCategories: readonly string[];
  /**
   * Category values treated as worker-safety / HR-WHS (04-04 §4b). Supplements
   * the primary `businessUnit == 'Staff'` signal for the `HR/WHS-touching`
   * token.
   */
  workerSafetyCategories: readonly string[];
}

// ---------------------------------------------------------------------------
// Safe structural readers (fields not on the shared spine)
// ---------------------------------------------------------------------------

/**
 * `category` lives on the per-register records (Incidents/Complaints/CI/
 * Feedback/Risk), not the shared `RegisterRecord` spine — read it structurally.
 */
function categoryOf(item: RegisterRecord): string | null {
  const c = (item as { category?: string | null }).category;
  return c ?? null;
}

/**
 * `reportedByEmail` (`ReportedByEmail`, the reporter's UPN — FIELDSET §1.5, ISS-018) exists only
 * on Incidents. It replaces the frozen Person column `ReportedBy`, which Graph returns as a
 * lookup id and so never matched (ISS-223). Other registers use the SharePoint author
 * (`createdBy`) for Own-only (matrix §4).
 */
function reportedByEmailOf(item: RegisterRecord): string | null {
  const r = (item as { reportedByEmail?: string | null }).reportedByEmail;
  return r ?? null;
}

/** A UPN for comparison: trimmed and lower-cased (D-13-05-5). Blank → null. */
function normalUpn(upn: string | null | undefined): string | null {
  const v = (upn ?? '').trim().toLowerCase();
  return v === '' ? null : v;
}

/**
 * Optional native `FinanceRelevant` flag — not yet in the schema (04-04 §4a),
 * read forward-compatibly so it wins the moment it is added, with no edit here.
 */
function financeFlagOf(item: RegisterRecord): boolean {
  return (item as { financeRelevant?: boolean }).financeRelevant === true;
}

// ---------------------------------------------------------------------------
// Per-token predicates (matrix §4). Each returns the items the token reveals.
// ---------------------------------------------------------------------------

type ScopePredicate = (
  user: Identity,
  register: RegisterKey,
  items: RegisterRecord[],
  config: ScopeConfig,
) => RegisterRecord[];

/** `All` — full oversight read, no filter (matrix §4). */
function scopeAll(
  _user: Identity,
  _register: RegisterKey,
  items: RegisterRecord[],
): RegisterRecord[] {
  return items;
}

/**
 * `Own only` — items the user lodged (matrix §4, the lodging-staff privacy
 * boundary). Incidents key off `reportedByEmail`, compared to the actor's UPN trimmed and
 * case-insensitively (D-13-05-5); a blank on either side never matches (fail closed). Every
 * other register keys off `createdBy` (the SharePoint author's email), compared the same way.
 */
function scopeOwnOnly(
  user: Identity,
  register: RegisterKey,
  items: RegisterRecord[],
): RegisterRecord[] {
  const me = normalUpn(user.upn);
  if (me === null) return [];
  if (register === 'incidents') {
    return items.filter((item) => normalUpn(reportedByEmailOf(item)) === me);
  }
  return items.filter((item) => normalUpn(item.createdBy) === me);
}

/**
 * `Portfolio` — the CM's managed business units (matrix §4):
 * `managingUnit ∈ {CM's BU set}` (falling back to `businessUnit` where
 * `managingUnit` is blank).
 *
 * FALLBACK (04-04-rbac-resolved §1 / 04-CONTEXT §8): until the per-CM BU map is
 * supplied, `user.businessUnits` is EMPTY and the Portfolio view is a single
 * `ManagingUnit`-grouped view ANY CM can filter — i.e. no hard per-CM
 * restriction, every item is returned for self-filtering. This widens nothing
 * (presentation scoping over a delegated response, §3) and is asserted
 * explicitly so the seam is visible when the real map lands.
 */
function scopePortfolio(
  user: Identity,
  _register: RegisterKey,
  items: RegisterRecord[],
): RegisterRecord[] {
  // No per-CM BU map yet → grouped fallback: return all (any CM self-filters).
  if (user.businessUnits.length === 0) return items;
  const set = new Set<BusinessUnit>(user.businessUnits);
  return items.filter((item) => {
    const unit = item.managingUnit ?? item.businessUnit;
    return unit !== null && set.has(unit);
  });
}

/**
 * `Finance-relevant` — items with a financial dimension (matrix §4): a native
 * `financeRelevant` flag (forward-compatible) OR a `category` in the INJECTED
 * finance subset (04-04 §4a). The subset is config, never a literal here.
 */
function scopeFinanceRelevant(
  _user: Identity,
  _register: RegisterKey,
  items: RegisterRecord[],
  config: ScopeConfig,
): RegisterRecord[] {
  const finance = new Set(config.financeCategories);
  return items.filter((item) => {
    if (financeFlagOf(item)) return true;
    const category = categoryOf(item);
    return category !== null && finance.has(category);
  });
}

/**
 * `HR/WHS-touching` — worker-safety / HR items (matrix §4):
 * `businessUnit == 'Staff'` OR `category` in the INJECTED worker-safety subset
 * (04-04 §4b). The subset is config, never a literal here.
 */
function scopeHrWhsTouching(
  _user: Identity,
  _register: RegisterKey,
  items: RegisterRecord[],
  config: ScopeConfig,
): RegisterRecord[] {
  const workerSafety = new Set(config.workerSafetyCategories);
  return items.filter((item) => {
    if (item.businessUnit === 'Staff') return true;
    const category = categoryOf(item);
    return category !== null && workerSafety.has(category);
  });
}

/**
 * The token → predicate map. Keyed by the exact `ScopeToken` union so the set of
 * handled tokens stays in lockstep with the matrix vocabulary. Any token NOT a
 * key here (unknown / undefined) hits the fail-closed default in `applyScope`.
 */
const SCOPE_PREDICATES: Record<ScopeToken, ScopePredicate> = {
  All: scopeAll,
  'Own only': scopeOwnOnly,
  Portfolio: scopePortfolio,
  'Finance-relevant': scopeFinanceRelevant,
  'HR/WHS-touching': scopeHrWhsTouching,
};

// ---------------------------------------------------------------------------
// The public entry point
// ---------------------------------------------------------------------------

/**
 * Filter `items` to what `token` permits `user` to see on `register`.
 *
 * Pure: same inputs → same output, no side effects. Empty input → empty output
 * (registers are empty today — never throws). Unknown / undefined token →
 * FAILS CLOSED with `[]` (never the full set).
 *
 * @param token    The scope token (from `defaultScopeFor(roles, register)`).
 * @param user     The signed-in identity ({ upn, businessUnits }).
 * @param register Which register the records belong to (drives Own-only key).
 * @param items    The register records (a delegated-Graph result, in code).
 * @param config   Injected subsets (finance / worker-safety categories).
 */
export function applyScope(
  token: ScopeToken,
  user: Identity,
  register: RegisterKey,
  items: RegisterRecord[],
  config: ScopeConfig,
): RegisterRecord[] {
  const predicate = SCOPE_PREDICATES[token];
  // Fail closed: an unknown / undefined token reveals NOTHING (never all).
  if (!predicate) return [];
  return predicate(user, register, items, config);
}

/** Re-exported for consumers that switch on the token vocabulary. */
export type { ScopeToken } from '../config/roles';
